import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PRIORITIES, api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Users2 } from "lucide-react";
import ParticipantsSection from "@/components/ParticipantsSection";
import AddParticipantDialog from "@/components/AddParticipantDialog";

export default function TaskDialog({
  open,
  onOpenChange,
  collaborators = [],
  projects = [],
  defaultCollaboratorId,
  defaultProjectId,
  defaultDirection = "to_me",
  onCreated,
}) {
  const [mode, setMode] = useState("owner");  // "owner" or "cross"
  const [form, setForm] = useState({
    title: "",
    description: "",
    collaborator_id: defaultCollaboratorId || "",
    from_collaborator_id: "",
    project_id: defaultProjectId || "",
    direction: defaultDirection,
    priority: "srednji",
    due_date: "",
  });
  const [saving, setSaving] = useState(false);
  const [participants, setParticipants] = useState([]);
  const [contactsById, setContactsById] = useState({});
  const [addOpen, setAddOpen] = useState(false);

  useEffect(() => {
    if (open) {
      setForm((f) => ({
        ...f,
        collaborator_id: defaultCollaboratorId || f.collaborator_id || collaborators[0]?.id || "",
        from_collaborator_id: "",
        project_id: defaultProjectId || "",
        direction: defaultDirection,
      }));
      setMode("owner");
      setParticipants([]);
      setContactsById({});
    }
  }, [open, defaultCollaboratorId, defaultProjectId, defaultDirection, collaborators]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    if (!form.title.trim()) return toast.error("Naslov je obavezan");
    if (!form.collaborator_id) return toast.error("Izaberi saradnika");
    if (mode === "cross") {
      if (!form.from_collaborator_id) return toast.error("Izaberi ko šalje (Od)");
      if (form.from_collaborator_id === form.collaborator_id) return toast.error("'Od' i 'Za' ne smeju biti isti saradnik");
    }
    setSaving(true);
    try {
      const payload = {
        title: form.title.trim(),
        description: form.description,
        collaborator_id: form.collaborator_id,
        from_collaborator_id: mode === "cross" ? form.from_collaborator_id : null,
        project_id: form.project_id || null,
        direction: mode === "cross" ? "to_them" : form.direction,
        priority: form.priority,
        due_date: form.due_date || null,
        participants: participants,
      };
      const { data } = await api.post("/tasks", payload);
      toast.success("Task dodat");
      onCreated?.(data);
      onOpenChange(false);
    } catch (e) {
      toast.error(formatApiError(e));
    }
    setSaving(false);
  };

  const otherCollabs = collaborators.filter((c) => c.id !== form.collaborator_id);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="task-dialog" className="bg-white max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-heading">Novi task</DialogTitle></DialogHeader>

        <div className="flex gap-1 p-1 bg-surface-secondary border border-line rounded-md">
          <button
            type="button"
            onClick={() => setMode("owner")}
            data-testid="mode-owner"
            className={`flex-1 text-xs py-1.5 rounded ${mode === "owner" ? "bg-white text-forest font-semibold shadow-sm" : "text-ink-muted"}`}
          >
            Ja ↔ saradnik
          </button>
          <button
            type="button"
            onClick={() => setMode("cross")}
            data-testid="mode-cross"
            className={`flex-1 text-xs py-1.5 rounded inline-flex items-center justify-center gap-1 ${mode === "cross" ? "bg-white text-forest font-semibold shadow-sm" : "text-ink-muted"}`}
          >
            <Users2 className="w-3 h-3" /> Saradnik → saradnik
          </button>
        </div>

        <div className="space-y-3">
          <div>
            <Label>Naslov</Label>
            <Input data-testid="task-title-input" value={form.title} onChange={(e) => set("title", e.target.value)} placeholder="npr. Poslati brief za snimanje" />
          </div>
          <div>
            <Label>Opis</Label>
            <Textarea data-testid="task-description-input" rows={2} value={form.description} onChange={(e) => set("description", e.target.value)} placeholder="Detalji (opcionalno)" />
          </div>

          {mode === "owner" ? (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Saradnik</Label>
                <Select value={form.collaborator_id} onValueChange={(v) => set("collaborator_id", v)}>
                  <SelectTrigger data-testid="task-collab-select" className="bg-white"><SelectValue placeholder="Izaberi" /></SelectTrigger>
                  <SelectContent className="bg-white">
                    {collaborators.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Smer</Label>
                <Select value={form.direction} onValueChange={(v) => set("direction", v)}>
                  <SelectTrigger data-testid="task-direction-select" className="bg-white"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white">
                    <SelectItem value="to_me">On duguje meni</SelectItem>
                    <SelectItem value="to_them">Ja dugujem njemu</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Od</Label>
                <Select value={form.from_collaborator_id} onValueChange={(v) => set("from_collaborator_id", v)}>
                  <SelectTrigger data-testid="task-from-select" className="bg-white"><SelectValue placeholder="Izaberi šalje" /></SelectTrigger>
                  <SelectContent className="bg-white">
                    {collaborators.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Za</Label>
                <Select value={form.collaborator_id} onValueChange={(v) => set("collaborator_id", v)}>
                  <SelectTrigger data-testid="task-to-select" className="bg-white"><SelectValue placeholder="Izaberi primaoca" /></SelectTrigger>
                  <SelectContent className="bg-white">
                    {otherCollabs.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Prioritet</Label>
              <Select value={form.priority} onValueChange={(v) => set("priority", v)}>
                <SelectTrigger data-testid="task-priority-select" className="bg-white"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white">
                  {PRIORITIES.map((p) => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Rok</Label>
              <Input data-testid="task-due-input" type="date" value={form.due_date} onChange={(e) => set("due_date", e.target.value)} />
            </div>
          </div>

          {projects.length > 0 && (
            <div>
              <Label>Projekat (opcionalno)</Label>
              <Select value={form.project_id || "none"} onValueChange={(v) => set("project_id", v === "none" ? "" : v)}>
                <SelectTrigger data-testid="task-project-select" className="bg-white"><SelectValue placeholder="Bez projekta" /></SelectTrigger>
                <SelectContent className="bg-white">
                  <SelectItem value="none">Bez projekta</SelectItem>
                  {projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}

          <ParticipantsSection
            participants={participants}
            contactsById={contactsById}
            onAdd={() => setAddOpen(true)}
            onRemove={(row) => setParticipants((ps) =>
              ps.filter((p) => !(p.contact_id === row.contact_id && p.role === row.role))
            )}
          />
        </div>

        <DialogFooter>
          <button className="btn-ghost" onClick={() => onOpenChange(false)} data-testid="task-cancel-btn">Otkaži</button>
          <button className="btn-primary" onClick={submit} disabled={saving} data-testid="task-save-btn">
            {saving ? "Čuvam..." : "Sačuvaj task"}
          </button>
        </DialogFooter>
      </DialogContent>

      <AddParticipantDialog
        open={addOpen}
        onOpenChange={setAddOpen}
        onPicked={({ contact, role }) => {
          setContactsById((m) => ({ ...m, [contact.id]: contact }));
          setParticipants((ps) => {
            if (ps.some((p) => p.contact_id === contact.id && p.role === role)) return ps;
            return [...ps, { contact_id: contact.id, role }];
          });
        }}
        excludeContactIds={participants.map((p) => p.contact_id)}
      />
    </Dialog>
  );
}
